/* ============================================================================
   SAKURA COLLECTORS CLUB — comportement de l'en-tête ORIPAYA
   ============================================================================

   Un seul custom element, aucune dépendance, aucune bibliothèque.
   Tout ce qui est ici est un ENRICHISSEMENT : sans ce fichier, l'en-tête reste
   entièrement navigable (panneaux au survol et au focus en CSS, drawer par
   ancre :target, formulaire de recherche qui poste vers /search).

   Responsabilités
     1. Mesure des hauteurs, réserve de place, --header-height
     2. En-tête collant : complet · compact · caché, en translate3d
     3. Barre d'annonce : fermeture mémorisée
     4. Navigation : intention de survol, aria-expanded, Échap, clic extérieur
     5. Recherche prédictive : debounce 250 ms, combobox, flèches, annonce vocale
     6. Menu mobile : ouverture, piège de focus, niveaux glissants
     7. Compteur de panier synchronisé avec le thème
   ============================================================================ */

(function () {
  'use strict';

  var SELECTEURS_FOCUSABLES =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var MOUVEMENT_REDUIT =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function estReduit() {
    return !!(MOUVEMENT_REDUIT && MOUVEMENT_REDUIT.matches);
  }

  class OripayaEntete extends HTMLElement {
    constructor() {
      super();
      this.dernierY = 0;
      this.tickEnCours = false;
      this.optionActive = -1;
      this.minuteurRecherche = null;
      this.controleur = null;
      this.pileNiveaux = [];
      this.declencheurMenu = null;
      this.minuteurSurvol = null;

      this.surDefilement = this.surDefilement.bind(this);
      this.surToucheGlobale = this.surToucheGlobale.bind(this);
      this.surClicGlobal = this.surClicGlobal.bind(this);
      this.surTabDrawer = this.surTabDrawer.bind(this);
    }

    connectedCallback() {
      this.fixe = this.querySelector('[data-ori-fixe]');
      this.sup = this.querySelector('[data-ori-sup]');
      this.reserve = this.querySelector('[data-ori-reserve]');
      this.barre = this.querySelector('[data-ori-barre]');

      this.initMesures();
      this.initCollant();
      this.initAnnonce();
      this.initNavigation();
      this.initRecherche();
      this.initMenuMobile();
      this.initPanier();

      document.addEventListener('keydown', this.surToucheGlobale);
      document.addEventListener('click', this.surClicGlobal);

      /* Éditeur de thème : sélectionner un bloc de mega-menu l'ouvre. */
      document.addEventListener('shopify:block:select', (e) => {
        var cible = this.querySelector('[data-ori-bloc="' + e.detail.blockId + '"]');
        if (cible) {
          var element = cible.closest('.ori-nav__element');
          if (element) this.ouvrirPanneau(element, true);
        }
      });
      document.addEventListener('shopify:block:deselect', () => this.fermerPanneaux());
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.surDefilement);
      document.removeEventListener('keydown', this.surToucheGlobale);
      document.removeEventListener('click', this.surClicGlobal);
      document.removeEventListener('keydown', this.surTabDrawer);
      if (this.observateurTaille) this.observateurTaille.disconnect();
      if (this.observateurPanier) this.observateurPanier.disconnect();
      if (this.controleur) this.controleur.abort();
    }

    /* ======================================================================
       1. MESURES
       La hauteur totale est mesurée à l'état complet et RÉSERVÉE par un bloc
       frère : c'est ce qui garantit zéro décalage quand l'en-tête se rétracte.
       ====================================================================== */

    initMesures() {
      this.mesurer();
      if ('ResizeObserver' in window && this.fixe) {
        this.observateurTaille = new ResizeObserver(() => this.mesurer());
        this.observateurTaille.observe(this.fixe);
        if (this.sup) this.observateurTaille.observe(this.sup);
      } else {
        window.addEventListener('resize', () => this.mesurer());
      }
      /* Les polices d'affichage changent la hauteur du logo textuel. */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.mesurer());
      }
    }

    mesurer() {
      if (!this.fixe) return;
      var total = Math.round(this.fixe.getBoundingClientRect().height);
      var sup = this.sup ? Math.round(this.sup.getBoundingClientRect().height) : 0;
      if (!total) return;

      this.style.setProperty('--ori-h-total', total + 'px');
      this.style.setProperty('--ori-h-sup', sup + 'px');
      /* Le thème Nexus pilote plusieurs positions collantes avec cette
         variable : on la tient à jour pour ne rien casser ailleurs. */
      document.body.style.setProperty('--header-height', total + 'px');
    }

    /* ======================================================================
       2. EN-TÊTE COLLANT
       ====================================================================== */

    initCollant() {
      if (this.dataset.oriCollant !== 'oui') return;
      this.seuil = parseInt(this.dataset.oriSeuil, 10) || 120;
      this.peutCacher = this.dataset.oriCacher === 'oui';
      this.dernierY = window.scrollY || 0;

      this.setAttribute('data-fixe', 'oui');
      this.setAttribute('data-etat', this.dernierY > this.seuil ? 'compact' : 'haut');

      window.addEventListener('scroll', this.surDefilement, { passive: true });
    }

    surDefilement() {
      if (this.tickEnCours) return;
      this.tickEnCours = true;
      window.requestAnimationFrame(() => {
        this.majEtatCollant();
        this.tickEnCours = false;
      });
    }

    majEtatCollant() {
      var y = Math.max(0, window.scrollY || 0);
      var delta = y - this.dernierY;

      /* Rien ne se rétracte tant qu'un panneau, la recherche ou le menu sont
         ouverts : faire disparaître l'en-tête sous les doigts serait hostile. */
      if (this.aUnPanneauOuvert()) {
        this.dernierY = y;
        return;
      }

      if (y <= this.seuil) {
        this.setAttribute('data-etat', 'haut');
        this.dernierY = y;
        return;
      }

      /* Amorti de 8px : sans lui, le moindre tremblement de trackpad ferait
         clignoter l'en-tête. */
      if (Math.abs(delta) < 8) return;

      if (delta > 0 && this.peutCacher) {
        this.setAttribute('data-etat', 'cache');
      } else {
        this.setAttribute('data-etat', 'compact');
      }
      this.dernierY = y;
    }

    aUnPanneauOuvert() {
      return (
        this.querySelector('.ori-nav__element[data-ouvert="true"]') !== null ||
        this.getAttribute('data-recherche') === 'ouverte' ||
        (this.recherche && this.recherche.getAttribute('data-ouverte') === 'oui') ||
        (this.menu && this.menu.getAttribute('aria-hidden') === 'false')
      );
    }

    /* ======================================================================
       3. BARRE D'ANNONCE
       La clé de stockage contient l'empreinte du message : un nouveau message
       réapparaît, un message déjà écarté ne revient pas.
       ====================================================================== */

    initAnnonce() {
      var annonce = this.querySelector('[data-ori-annonce]');
      if (!annonce) return;
      var bouton = annonce.querySelector('[data-ori-annonce-fermer]');
      if (!bouton) return;
      var cle = annonce.getAttribute('data-ori-annonce');

      bouton.addEventListener('click', () => {
        annonce.hidden = true;
        try {
          window.localStorage.setItem(cle, '1');
        } catch (e) {
          /* Navigation privée ou stockage refusé : la fermeture reste valable
             pour la session en cours, ce qui suffit. */
        }
        this.mesurer();
      });
    }

    /* ======================================================================
       4. NAVIGATION DESKTOP
       Le CSS ouvre déjà les panneaux au survol et au focus. Le JS ajoute
       l'intention (délais), l'état ARIA, Échap et le clic extérieur.
       ====================================================================== */

    initNavigation() {
      this.elementsNav = Array.prototype.slice.call(
        this.querySelectorAll('.ori-nav__element[data-ori-panneau]')
      );

      this.elementsNav.forEach((element) => {
        var declencheur = element.querySelector('.ori-nav__lien');
        if (!declencheur) return;

        element.addEventListener('mouseenter', () => {
          window.clearTimeout(this.minuteurSurvol);
          this.minuteurSurvol = window.setTimeout(() => this.ouvrirPanneau(element), 90);
        });

        element.addEventListener('mouseleave', () => {
          window.clearTimeout(this.minuteurSurvol);
          this.minuteurSurvol = window.setTimeout(() => this.fermerPanneau(element), 220);
        });

        element.addEventListener('focusin', () => this.ouvrirPanneau(element));
        element.addEventListener('focusout', (e) => {
          if (!element.contains(e.relatedTarget)) this.fermerPanneau(element);
        });
      });
    }

    ouvrirPanneau(element, forcer) {
      this.elementsNav.forEach((autre) => {
        if (autre !== element) this.fermerPanneau(autre);
      });
      element.setAttribute('data-ouvert', 'true');
      var declencheur = element.querySelector('.ori-nav__lien');
      if (declencheur) declencheur.setAttribute('aria-expanded', 'true');
      if (forcer) this.setAttribute('data-etat', 'haut');
    }

    fermerPanneau(element) {
      element.removeAttribute('data-ouvert');
      var declencheur = element.querySelector('.ori-nav__lien');
      if (declencheur) declencheur.setAttribute('aria-expanded', 'false');
    }

    fermerPanneaux() {
      if (!this.elementsNav) return;
      this.elementsNav.forEach((element) => this.fermerPanneau(element));
    }

    /* ======================================================================
       5. RECHERCHE PRÉDICTIVE
       Route Shopify /search/suggest rendue par une section Liquid : les prix,
       les devises, les traductions et la disponibilité restent calculés par
       Shopify, jamais reconstruits en JavaScript.
       ====================================================================== */

    initRecherche() {
      this.recherche = this.querySelector('[data-ori-recherche]');
      if (!this.recherche) return;

      this.champ = this.recherche.querySelector('[data-ori-champ]');
      this.panneauRecherche = this.recherche.querySelector('[data-ori-resultats]');
      this.statutRecherche = this.recherche.querySelector('[data-ori-statut]');
      this.formRecherche = this.recherche.querySelector('form');
      this.gabaritVide = this.recherche.querySelector('[data-ori-vide]');
      if (!this.champ || !this.panneauRecherche) return;

      this.urlSuggest = this.recherche.getAttribute('data-ori-url') || '/search/suggest';
      this.sectionSuggest = this.recherche.getAttribute('data-ori-section') || '';
      this.limiteSuggest = this.recherche.getAttribute('data-ori-limite') || '5';

      this.champ.addEventListener('input', () => {
        this.recherche.setAttribute('data-remplie', this.champ.value ? 'oui' : 'non');
        window.clearTimeout(this.minuteurRecherche);
        var terme = this.champ.value.trim();

        if (terme.length < 2) {
          this.afficherEtatVide();
          return;
        }
        /* 250 ms : au-dessus la frappe paraît molle, en dessous on interroge
           Shopify à chaque lettre pour rien. */
        this.minuteurRecherche = window.setTimeout(() => this.interroger(terme), 250);
      });

      this.champ.addEventListener('focus', () => {
        if (this.champ.value.trim().length < 2) this.afficherEtatVide();
        else this.ouvrirRecherche();
      });

      this.champ.addEventListener('keydown', (e) => this.surToucheRecherche(e));

      var effacer = this.recherche.querySelector('[data-ori-effacer]');
      if (effacer) {
        effacer.addEventListener('click', () => {
          this.champ.value = '';
          this.recherche.setAttribute('data-remplie', 'non');
          this.afficherEtatVide();
          this.champ.focus();
        });
      }

      /* Bascule mobile : sans JS le bouton est un lien vers /search. */
      var bascule = this.querySelector('[data-ori-recherche-bascule]');
      if (bascule) {
        bascule.addEventListener('click', (e) => {
          e.preventDefault();
          this.setAttribute('data-recherche', 'ouverte');
          bascule.setAttribute('aria-expanded', 'true');
          this.setAttribute('data-etat', 'haut');
          window.setTimeout(() => this.champ.focus(), 60);
        });
      }
      var fermerMobile = this.querySelector('[data-ori-recherche-fermer]');
      if (fermerMobile) {
        fermerMobile.addEventListener('click', () => this.fermerRechercheMobile());
      }
    }

    fermerRechercheMobile() {
      this.removeAttribute('data-recherche');
      this.fermerRecherche();
      var bascule = this.querySelector('[data-ori-recherche-bascule]');
      if (bascule) {
        bascule.setAttribute('aria-expanded', 'false');
        bascule.focus();
      }
    }

    ouvrirRecherche() {
      this.recherche.setAttribute('data-ouverte', 'oui');
      this.champ.setAttribute('aria-expanded', 'true');
    }

    fermerRecherche() {
      this.recherche.setAttribute('data-ouverte', 'non');
      this.champ.setAttribute('aria-expanded', 'false');
      this.champ.removeAttribute('aria-activedescendant');
      this.optionActive = -1;
    }

    afficherEtatVide() {
      if (this.gabaritVide && this.gabaritVide.innerHTML.trim()) {
        this.panneauRecherche.innerHTML = this.gabaritVide.innerHTML;
        this.ouvrirRecherche();
      } else {
        this.fermerRecherche();
      }
      this.optionActive = -1;
      this.champ.removeAttribute('aria-activedescendant');
    }

    interroger(terme) {
      if (this.controleur) this.controleur.abort();
      this.controleur = typeof AbortController !== 'undefined' ? new AbortController() : null;

      var params =
        'q=' + encodeURIComponent(terme) +
        '&resources[type]=product,collection,query' +
        '&resources[limit]=' + encodeURIComponent(this.limiteSuggest) +
        '&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=title,product_type,variants.title,vendor,tag';

      if (this.sectionSuggest) params += '&section_id=' + encodeURIComponent(this.sectionSuggest);

      this.panneauRecherche.setAttribute('aria-busy', 'true');

      fetch(this.urlSuggest + '?' + params, {
        signal: this.controleur ? this.controleur.signal : undefined
      })
        .then((reponse) => {
          if (!reponse.ok) throw new Error('suggest');
          return reponse.text();
        })
        .then((html) => {
          var gabarit = document.createElement('div');
          gabarit.innerHTML = html;
          var bloc = gabarit.querySelector('[data-ori-suggestions]');
          this.panneauRecherche.innerHTML = bloc ? bloc.innerHTML : '';
          this.panneauRecherche.setAttribute('aria-busy', 'false');
          this.optionActive = -1;
          this.champ.removeAttribute('aria-activedescendant');
          this.ouvrirRecherche();
          this.annoncerResultats(bloc);
        })
        .catch((erreur) => {
          if (erreur && erreur.name === 'AbortError') return;
          this.panneauRecherche.setAttribute('aria-busy', 'false');
          /* En cas d'échec réseau on n'invente rien : le formulaire natif
             reste la voie de secours, et on le dit. */
          this.fermerRecherche();
        });
    }

    annoncerResultats(bloc) {
      if (!this.statutRecherche) return;
      var total = bloc ? bloc.getAttribute('data-ori-total') : null;
      var message = bloc ? bloc.getAttribute('data-ori-message') : '';
      this.statutRecherche.textContent = message || (total ? total : '');
    }

    optionsRecherche() {
      return Array.prototype.slice.call(
        this.panneauRecherche.querySelectorAll('[role="option"]')
      );
    }

    surToucheRecherche(e) {
      var options = this.optionsRecherche();

      if (e.key === 'Escape') {
        if (this.recherche.getAttribute('data-ouverte') === 'oui') {
          e.preventDefault();
          this.fermerRecherche();
        } else if (this.getAttribute('data-recherche') === 'ouverte') {
          this.fermerRechercheMobile();
        }
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!options.length) return;
        e.preventDefault();
        this.ouvrirRecherche();
        var pas = e.key === 'ArrowDown' ? 1 : -1;
        this.optionActive = (this.optionActive + pas + options.length + 1) % (options.length + 1);
        /* L'index égal à la longueur = retour au champ, sans option active. */
        this.majOptionActive(options);
        return;
      }

      if (e.key === 'Enter') {
        if (this.optionActive >= 0 && options[this.optionActive]) {
          e.preventDefault();
          options[this.optionActive].click();
        }
        /* Sinon : le formulaire poste normalement vers /search. */
      }
    }

    majOptionActive(options) {
      options.forEach((option, index) => {
        if (index === this.optionActive) {
          option.setAttribute('data-actif', 'oui');
          this.champ.setAttribute('aria-activedescendant', option.id);
          if (option.scrollIntoView) option.scrollIntoView({ block: 'nearest' });
        } else {
          option.removeAttribute('data-actif');
        }
      });
      if (this.optionActive < 0 || this.optionActive >= options.length) {
        this.champ.removeAttribute('aria-activedescendant');
      }
    }

    /* ======================================================================
       6. MENU MOBILE
       ====================================================================== */

    initMenuMobile() {
      this.menu = this.querySelector('[data-ori-menu]');
      if (!this.menu) return;

      this.panneauMenu = this.menu.querySelector('.sk-drawer__panneau');

      var ouvreurs = this.querySelectorAll('[data-ori-menu-ouvrir]');
      Array.prototype.forEach.call(ouvreurs, (bouton) => {
        bouton.addEventListener('click', (e) => {
          e.preventDefault();
          this.ouvrirMenu(bouton);
        });
      });

      Array.prototype.forEach.call(this.menu.querySelectorAll('[data-sk-fermer]'), (bouton) => {
        bouton.addEventListener('click', (e) => {
          e.preventDefault();
          this.fermerMenu();
        });
      });

      /* Niveaux glissants : chaque bouton ouvre le sous-panneau qu'il annonce. */
      Array.prototype.forEach.call(this.menu.querySelectorAll('[data-ori-niveau-ouvrir]'), (bouton) => {
        bouton.addEventListener('click', () => {
          var cible = this.menu.querySelector('#' + bouton.getAttribute('aria-controls'));
          if (!cible) return;
          cible.setAttribute('data-ouvert', 'true');
          bouton.setAttribute('aria-expanded', 'true');
          this.pileNiveaux.push({ panneau: cible, declencheur: bouton });
          var retour = cible.querySelector('[data-ori-niveau-retour]');
          if (retour) window.setTimeout(() => retour.focus(), estReduit() ? 0 : 220);
        });
      });

      Array.prototype.forEach.call(this.menu.querySelectorAll('[data-ori-niveau-retour]'), (bouton) => {
        bouton.addEventListener('click', () => this.reculerNiveau());
      });
    }

    reculerNiveau() {
      var dernier = this.pileNiveaux.pop();
      if (!dernier) return;
      dernier.panneau.removeAttribute('data-ouvert');
      dernier.declencheur.setAttribute('aria-expanded', 'false');
      dernier.declencheur.focus();
    }

    ouvrirMenu(declencheur) {
      this.declencheurMenu = declencheur || document.activeElement;
      this.menu.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('sk-scroll-bloque');
      this.basculerInert(true);
      document.addEventListener('keydown', this.surTabDrawer);
      if (declencheur) declencheur.setAttribute('aria-expanded', 'true');
      if (this.panneauMenu) {
        window.setTimeout(() => this.panneauMenu.focus(), estReduit() ? 0 : 60);
      }
    }

    fermerMenu() {
      this.menu.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('sk-scroll-bloque');
      this.basculerInert(false);
      document.removeEventListener('keydown', this.surTabDrawer);

      /* On referme tous les niveaux pour que la prochaine ouverture reparte
         de la racine, comme le ferait un tiroir qu'on remet en place. */
      while (this.pileNiveaux.length) {
        var niveau = this.pileNiveaux.pop();
        niveau.panneau.removeAttribute('data-ouvert');
        niveau.declencheur.setAttribute('aria-expanded', 'false');
      }

      if (this.declencheurMenu) {
        this.declencheurMenu.setAttribute('aria-expanded', 'false');
        this.declencheurMenu.focus();
        this.declencheurMenu = null;
      }
    }

    basculerInert(actif) {
      var section = this.closest('.shopify-section') || this;
      Array.prototype.forEach.call(document.body.children, (enfant) => {
        if (enfant === section || enfant.contains(this.menu)) return;
        if (actif) enfant.setAttribute('inert', '');
        else enfant.removeAttribute('inert');
      });
      /* À l'intérieur de ma propre section, seule la barre est neutralisée. */
      if (this.fixe) {
        if (actif) this.fixe.setAttribute('inert', '');
        else this.fixe.removeAttribute('inert');
      }
    }

    /* Les niveaux fermés sont en visibility:hidden, donc déjà retirés de
       l'arbre d'accessibilité ; il reste à les retirer de l'ordre de
       tabulation, ainsi que le niveau parent recouvert par un sous-niveau. */
    focusablesMenu() {
      var actif = this.pileNiveaux.length
        ? this.pileNiveaux[this.pileNiveaux.length - 1].panneau
        : null;

      return Array.prototype.slice
        .call(this.panneauMenu.querySelectorAll(SELECTEURS_FOCUSABLES))
        .filter(function (element) {
          if (element.closest('.ori-menu__sous:not([data-ouvert="true"])')) return false;
          if (!actif) return true;
          if (element.closest('.ori-menu__sous') === actif) return true;
          /* En-tête et pied du tiroir : toujours atteignables. */
          return !element.closest('.sk-drawer__corps');
        });
    }

    /* Piège de focus : indispensable, `inert` n'étant pas universel. */
    surTabDrawer(e) {
      if (e.key !== 'Tab' || !this.panneauMenu) return;
      var focusables = this.focusablesMenu();
      if (!focusables.length) return;
      var premier = focusables[0];
      var dernier = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === premier) {
        e.preventDefault();
        dernier.focus();
      } else if (!e.shiftKey && document.activeElement === dernier) {
        e.preventDefault();
        premier.focus();
      }
    }

    /* ======================================================================
       7. COMPTEUR DE PANIER
       Le thème remplace le contenu de .cart-count-text après chaque ajout :
       on observe ce nœud plutôt que d'écouter des événements privés.
       ====================================================================== */

    initPanier() {
      var compteur = this.querySelector('.cart-count-text');
      if (!compteur) return;
      var action = compteur.closest('.ori-action');
      if (!action) return;

      var majEtat = function () {
        var texte = compteur.querySelector('.cc_text');
        var valeur = texte ? parseInt(texte.textContent.trim(), 10) : 0;
        action.setAttribute('data-vide', !valeur ? 'oui' : 'non');
      };

      majEtat();
      this.observateurPanier = new MutationObserver(majEtat);
      this.observateurPanier.observe(compteur, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    /* ======================================================================
       ÉCOUTEURS GLOBAUX
       ====================================================================== */

    surToucheGlobale(e) {
      if (e.key !== 'Escape') return;

      if (this.menu && this.menu.getAttribute('aria-hidden') === 'false') {
        if (this.pileNiveaux.length) this.reculerNiveau();
        else this.fermerMenu();
        return;
      }

      var ouvert = this.querySelector('.ori-nav__element[data-ouvert="true"]');
      if (ouvert) {
        var declencheur = ouvert.querySelector('.ori-nav__lien');
        this.fermerPanneau(ouvert);
        if (declencheur) declencheur.focus();
        return;
      }

      if (this.recherche && this.recherche.getAttribute('data-ouverte') === 'oui') {
        this.fermerRecherche();
      } else if (this.getAttribute('data-recherche') === 'ouverte') {
        this.fermerRechercheMobile();
      }
    }

    surClicGlobal(e) {
      if (this.recherche && !this.recherche.contains(e.target)) this.fermerRecherche();
      if (!this.contains(e.target)) this.fermerPanneaux();
    }
  }

  if (!customElements.get('oripaya-entete')) {
    customElements.define('oripaya-entete', OripayaEntete);
  }
})();
